// A port of SuiteEditor's MarkdownSpans: the prose dialect's spans in source text, by the same rules as
// `ProseMarkdown.parse` (Web/core), so what the editor shows always matches what is stored and compiled. Offsets are
// UTF-16 code units, exactly as NSString's, so the Swift logic carries over line for line.
import type { Alignment, InkColor, ListStyle } from '@writing-suite/core';
import { INK_COLORS, isLinkAddress, paragraphFence, parseListMarker as coreListMarker, trimCFWhitespace } from '@writing-suite/core';

export type Fence = { kind: 'epigraph' } | { kind: 'aligned'; alignment: Alignment } | { kind: 'verse'; alignment: Alignment | null };

export type Kind =
  | 'marker' | 'inlineMarker' | 'italic' | 'bold' | 'smallCaps' | 'underline'
  | 'highlight' | 'textColor' | 'link' | 'footnoteReference' | 'listMarker'
  | 'blankLine' | 'stanzaBreak' | 'sceneBreak' | 'blockQuote' | 'epigraph' | 'footnoteDefinition' | 'aligned' | 'verse' | 'listItem';

/** A span: `ink` for a highlight or text colour, `alignment` for an aligned line (or a verse line's, null when flush
 *  left), `address` for a link's words (its `[` and `](address){…}` are `inlineMarker`s). */
export interface Span { kind: Kind; from: number; to: number; ink?: InkColor; alignment?: Alignment | null; address?: string }

const STAR = 42, BACKSLASH = 92, OPEN = 91, CLOSE = 93, CARET = 94, NL = 10, OBRACE = 123, CBRACE = 125, GT = 62, SPACE = 32;
const OPAREN = 40, CPAREN = 41, TAB = 9, LINE_SEPARATOR = 0x2028;

/** `trimmingCharacters(in: .whitespaces)`, with a fast path for lines that need nothing trimmed. */
export function trimmed(line: string): string {
  if (line.length === 0) return line;
  const a = line.charCodeAt(0), b = line.charCodeAt(line.length - 1);
  if (a > 32 && b > 32 && a !== 0xa0 && b !== 0xa0 && a < 0x1680 && b < 0x1680) return line;
  return trimCFWhitespace(line);
}

// MARK: span styles (ProseMarkdown.InlineStyle without emphasis)

export interface SpanStyle { smallCaps: boolean; underline: boolean; highlight: InkColor | null; color: InkColor | null }
const noSpan = (): SpanStyle => ({ smallCaps: false, underline: false, highlight: null, color: null });
const isInk = (s: string): s is InkColor => (INK_COLORS as readonly string[]).includes(s);

/** `InlineStyle.span(classes:)`: `{.sc .u .hl-yellow .c-red}`, only classes the dialect knows. */
export function spanFromClasses(classes: string): SpanStyle | null {
  if (!classes.startsWith('{') || !classes.endsWith('}')) return null;
  const names = classes.slice(1, -1).split(' ').filter((n) => n.length > 0);
  if (names.length === 0) return null;
  const s = noSpan();
  for (const n of names) {
    if (n === '.sc') s.smallCaps = true;
    else if (n === '.u') s.underline = true;
    else if (n.startsWith('.hl-') && isInk(n.slice(4))) s.highlight = n.slice(4) as InkColor;
    else if (n.startsWith('.c-') && isInk(n.slice(3))) s.color = n.slice(3) as InkColor;
    else return null;
  }
  return s;
}

export function spanClasses(s: SpanStyle): string | null {
  const names: string[] = [];
  if (s.smallCaps) names.push('.sc');
  if (s.underline) names.push('.u');
  if (s.highlight) names.push('.hl-' + s.highlight);
  if (s.color) names.push('.c-' + s.color);
  return names.length === 0 ? null : '{' + names.join(' ') + '}';
}

function spanEqual(a: SpanStyle, b: SpanStyle) {
  return a.smallCaps === b.smallCaps && a.underline === b.underline && a.highlight === b.highlight && a.color === b.color;
}

// MARK: lists

/** `ProseMarkdown.ListStyle.parse(line:continuing:)` (Web/core), with a cheap test first: a marker ends in a tab
 *  within its first 12 characters. `length` is in UTF-16 units (list markers are all single units). */
export function parseListMarker(line: string, continuing: ListStyle | null = null): { style: ListStyle; number: number; length: number } | null {
  const head = line.length > 13 ? line.slice(0, 13) : line;
  if (head.indexOf('\t') < 0) return null;
  return coreListMarker(line, continuing);
}

// MARK: block scanning, one line at a time

function fenceOf(t: string): Fence | null {
  if (t === '::: epigraph') return { kind: 'epigraph' };
  const f = paragraphFence(t);
  if (!f) return null;
  if (f.kind.type === 'verse') return { kind: 'verse', alignment: f.alignment };
  return f.alignment ? { kind: 'aligned', alignment: f.alignment } : null;
}
export { fenceOf as fence };

export const isSceneBreak = (t: string) => t === '***' || t === '* * *';

/** A line that isn't body prose: a scene break, quotation, fence, footnote or list item (`EditorStyler.isBlockLine`). */
export function isBlockLine(c: string) {
  return c.startsWith('>') || c.startsWith('[^') || c.startsWith(':::') || isSceneBreak(c) || parseListMarker(c) !== null;
}

/** The scanner's state between lines (`MarkdownSpans.scan`) plus the book indent rule's (`EditorStyler.indentParagraphs`),
 *  so a viewport can be styled from any line once the state at it is known. */
export class LineScanner {
  fence: Fence | null = null;
  list: ListStyle | null = null;
  blockStart = true;
  afterHardBreak = false;
  // Indent rule (`leadIn`): a fragment's start counts as after a blank line and after a block.
  afterBlank = true;
  afterBlock = true;
  inIndentFence = false;

  clone(): LineScanner { return Object.assign(new LineScanner(), this); }

  /** Spans of one line (without its newline) starting at `base`, pushed to `out`; returns whether the line is the
   *  first of an indented book paragraph. */
  line(text: string, base: number, out: Span[]): boolean {
    const t = trimmed(text);
    const startsBlock = this.blockStart;
    this.blockStart = false;
    const end = base + text.length;
    const inline = (from: number) => inlineSpans(text, from - base, text.length, base, out);
    if (this.fence) {
      if (t === ':::') { out.push({ kind: 'marker', from: base, to: end }); this.fence = null; }
      // A blank line in verse parts two stanzas (`stanzaBreak`), shown a line high as compile sets them apart.
      else if (t.length === 0 && this.fence.kind !== 'epigraph') out.push({ kind: this.fence.kind === 'verse' ? 'stanzaBreak' : 'blankLine', from: base, to: end });
      else {
        if (this.fence.kind === 'epigraph') out.push({ kind: 'epigraph', from: base, to: end });
        else if (this.fence.kind === 'verse') out.push({ kind: 'verse', from: base, to: end, alignment: this.fence.alignment });
        else out.push({ kind: 'aligned', from: base, to: end, alignment: this.fence.alignment });
        inline(base);
      }
    } else if (t.length === 0) {
      out.push({ kind: 'blankLine', from: base, to: end });
      this.blockStart = true;
      this.list = null;
    } else if (isSceneBreak(t)) {
      out.push({ kind: 'sceneBreak', from: base, to: end });
    } else if (fenceOf(t)) {
      out.push({ kind: 'marker', from: base, to: end });
      this.fence = fenceOf(t);
    } else {
      const marker = (startsBlock || this.list !== null) && !this.afterHardBreak ? parseListMarker(text, this.list) : null;
      if (marker) {
        this.list = marker.style;
        out.push({ kind: 'listItem', from: base, to: end }, { kind: 'listMarker', from: base, to: base + marker.length });
        inline(base + marker.length);
      } else if (this.list !== null && !text.startsWith('>')) {
        out.push({ kind: 'listItem', from: base, to: end });
        inline(base);
      } else if (text.startsWith('>')) {
        const m = text.startsWith('> ') ? 2 : 1;
        out.push({ kind: 'blockQuote', from: base, to: end }, { kind: 'marker', from: base, to: base + m });
        inline(base + m);
      } else if (text.startsWith('[^') && text.indexOf(']:') >= 0) {
        const m = text.indexOf(']:') + 2;
        out.push({ kind: 'footnoteDefinition', from: base, to: end }, { kind: 'marker', from: base, to: base + m });
        inline(base + m);
      } else {
        inline(base);
      }
    }
    this.afterHardBreak = text.endsWith('\\') && !text.endsWith('\\\\');
    return this.indentRule(t);
  }

  private indentRule(content: string): boolean {
    if (content.length === 0) { this.afterBlank = true; return false; }
    const afterBlank = this.afterBlank;
    this.afterBlank = false;
    if (content.startsWith(':::')) { this.inIndentFence = !this.inIndentFence; this.afterBlock = true; return false; }
    if (this.inIndentFence || isBlockLine(content)) { this.afterBlock = true; return false; }
    if (!afterBlank) return false;
    if (this.afterBlock) { this.afterBlock = false; return false; }
    return true;
  }
}

/** `MarkdownSpans.scan` over a whole string. */
export function scan(text: string): Span[] {
  const out: Span[] = [];
  const s = new LineScanner();
  let start = 0;
  for (;;) {
    let end = text.indexOf('\n', start);
    if (end < 0) end = text.length;
    s.line(text.slice(start, end), start, out);
    if (end >= text.length) break;
    start = end + 1;
  }
  return out;
}

// MARK: inline spans

/** A span's close: where its `]` is, how long `]{…}` (or a link's `](address){…}`) runs, the styles its classes give,
 *  and a link's address (null for a span). */
interface SpanClose { location: number; length: number; style: SpanStyle; address: string | null }

/** The close of a link opened just before `start` (`MarkdownSpans.linkClose`), as `ProseMarkdown.linkClose` finds it:
 *  the first unescaped `]` (no `[` before it) followed by `(address)` with a known scheme, and classes the dialect knows
 *  after it if any. */
function linkClose(t: string, start: number, end: number): SpanClose | null {
  let i = start;
  while (i < end) {
    const c = t.charCodeAt(i);
    if (c === BACKSLASH) { i += 2; continue; }
    if (c === OPEN) return null;
    if (c === CLOSE) {
      if (i + 1 >= end || t.charCodeAt(i + 1) !== OPAREN) return null;
      let j = i + 2;
      let depth = 0;
      let address = '';
      while (j < end) {
        const d = t.charCodeAt(j);
        if (d === BACKSLASH && j + 1 < end) { address += t[j + 1]; j += 2; continue; }
        if (d === SPACE || d === TAB || d === LINE_SEPARATOR) return null;
        if (d === OPAREN) depth++;
        if (d === CPAREN) {
          if (depth === 0) break;
          depth--;
        }
        address += t[j];
        j++;
      }
      if (j >= end || !isLinkAddress(address)) return null;
      let close = j + 1;
      let style = noSpan();
      if (close < end && t.charCodeAt(close) === OBRACE) {
        let k = close + 1;
        while (k < end && t.charCodeAt(k) !== CBRACE) k++;
        const classes = k < end ? spanFromClasses(t.slice(close, k + 1)) : null;
        if (classes) { style = classes; close = k + 1; }
      }
      return { location: i, length: close - i, style, address };
    }
    i++;
  }
  return null;
}

/** A link's close, else a span's, for a `[` just before `start`. */
const bracketClose = (t: string, start: number, end: number) => linkClose(t, start, end) ?? spanClose(t, start, end);

/** The close of a span opened just before `start` (`MarkdownSpans.spanClose`): the first unescaped `]` (no `[`
 *  before it) followed by `{…}` classes the dialect knows. */
function spanClose(t: string, start: number, end: number): SpanClose | null {
  let i = start;
  while (i < end) {
    const c = t.charCodeAt(i);
    if (c === BACKSLASH) { i += 2; continue; }
    if (c === OPEN) return null;
    if (c === CLOSE) {
      if (i + 1 >= end || t.charCodeAt(i + 1) !== OBRACE) return null;
      let j = i + 2;
      while (j < end && t.charCodeAt(j) !== CBRACE) j++;
      if (j >= end) return null;
      const style = spanFromClasses(t.slice(i + 1, j + 1));
      return style ? { location: i, length: j - i + 1, style, address: null } : null;
    }
    i++;
  }
  return null;
}

/** Inline spans of `t[from, end)`, offsets shifted by `base` (`MarkdownSpans.inline`). */
export function inlineSpans(t: string, from: number, end: number, base: number, out: Span[]) {
  let italic: number | null = null, bold: number | null = null;
  let span: { start: number; close: SpanClose } | null = null;
  const push = (kind: Kind, a: number, b: number, ink?: InkColor) => out.push(ink ? { kind, from: a + base, to: b + base, ink } : { kind, from: a + base, to: b + base });
  let i = from;
  while (i < end) {
    const c = t.charCodeAt(i);
    if (c === BACKSLASH && i + 1 < end) { push('inlineMarker', i, i + 1); i += 2; continue; }
    if (c === STAR) {
      let n = 0;
      while (i + n < end && t.charCodeAt(i + n) === STAR) n++;
      push('inlineMarker', i, i + n);
      const after = i + n;
      if (n % 2 === 1) { if (italic !== null) { if (i > italic) push('italic', italic, i); italic = null; } else italic = after; }
      if (n >= 2) { if (bold !== null) { if (i > bold) push('bold', bold, i); bold = null; } else bold = after; }
      i = after;
      continue;
    }
    if (c === OPEN && i + 1 < end && t.charCodeAt(i + 1) === CARET) {
      let j = i + 2;
      while (j < end && t.charCodeAt(j) !== CLOSE) j++;
      if (j < end) { push('footnoteReference', i, j + 1); i = j + 1; continue; }
    }
    if (c === OPEN && span === null) {
      const close = bracketClose(t, i + 1, end);
      if (close) { push('inlineMarker', i, i + 1); span = { start: i + 1, close }; i++; continue; }
    }
    if (c === CLOSE && span !== null && span.close.location === i) {
      const { start, close } = span;
      span = null;
      if (i > start) {
        if (close.style.smallCaps) push('smallCaps', start, i);
        if (close.style.underline) push('underline', start, i);
        if (close.style.highlight) push('highlight', start, i, close.style.highlight);
        if (close.style.color) push('textColor', start, i, close.style.color);
        if (close.address !== null) out.push({ kind: 'link', from: start + base, to: i + base, address: close.address });
      }
      push('inlineMarker', i, i + close.length);
      i += close.length;
      continue;
    }
    i++;
  }
  // Unclosed emphasis runs to the end of the line, as the parser treats it.
  if (italic !== null && end > italic) push('italic', italic, end);
  if (bold !== null && end > bold) push('bold', bold, end);
}

// MARK: inline state

/** The inline styles in force at a place (`MarkdownSpans.InlineState`): emphasis, what a span carries, and the address of
 *  the link the text is in. */
export interface InlineState extends SpanStyle { italic: boolean; bold: boolean; link: string | null }
export const plain = (): InlineState => ({ italic: false, bold: false, ...noSpan(), link: null });
export const stateEqual = (a: InlineState, b: InlineState) => a.italic === b.italic && a.bold === b.bold && spanEqual(a, b) && a.link === b.link;
export const isPlain = (s: InlineState) => stateEqual(s, plain());

const lineStartOf = (t: string, i: number) => (i <= 0 ? 0 : t.lastIndexOf('\n', i - 1) + 1);
function lineEndOf(t: string, i: number) { const e = t.indexOf('\n', i); return e < 0 ? t.length : e; }

/** The inline styles in force at `location`: what text inserted there would look like (`MarkdownSpans.state`). */
export function stateAt(t: string, location: number): InlineState {
  location = Math.min(location, t.length);
  const lineStart = lineStartOf(t, location);
  const lineEnd = lineEndOf(t, location);
  const state = plain();
  let i = lineStart;
  let spanEnd: SpanClose | null = null;
  // A block quote's `> ` isn't inline syntax.
  if (t.length > i && t.charCodeAt(i) === GT) i += i + 1 < lineEnd && t.charCodeAt(i + 1) === SPACE ? 2 : 1;
  while (i < location) {
    const c = t.charCodeAt(i);
    if (c === BACKSLASH && i + 1 < lineEnd) { i += 2; continue; }
    if (c === STAR) {
      let n = 0;
      while (i + n < lineEnd && t.charCodeAt(i + n) === STAR) n++;
      if (n % 2 === 1) state.italic = !state.italic;
      if (n >= 2) state.bold = !state.bold;
      i += n;
      continue;
    }
    if (c === OPEN && i + 1 < lineEnd && t.charCodeAt(i + 1) === CARET) {
      let j = i + 2;
      while (j < lineEnd && t.charCodeAt(j) !== CLOSE) j++;
      if (j < lineEnd) { i = j + 1; continue; }
    }
    if (c === OPEN && spanEnd === null) {
      const close = bracketClose(t, i + 1, lineEnd);
      if (close) { Object.assign(state, close.style); state.link = close.address; spanEnd = close; i++; continue; }
    }
    if (c === CLOSE && spanEnd !== null && spanEnd.location === i) {
      // Text inserted inside a close's `{…}` (or a link's address) would be read as part of it: it belongs after.
      Object.assign(state, noSpan());
      state.link = null;
      i += spanEnd.length;
      spanEnd = null;
      continue;
    }
    i++;
  }
  return state;
}

/** The markers that take text from one inline state to another (`MarkdownSpans.transition`): a span or link closed
 *  (with its address and classes), emphasis toggled (one run of 1–3 asterisks), a span or link opened. */
export function transition(a: InlineState, b: InlineState): string {
  let out = '';
  const spanChanges = !spanEqual(a, b) || a.link !== b.link;
  const ac = spanClasses(a);
  if (spanChanges) {
    if (a.link !== null) out += '](' + a.link + ')' + (ac ?? '');
    else if (ac) out += ']' + ac;
  }
  const di = a.italic !== b.italic, db = a.bold !== b.bold;
  if (di && db) out += '***'; else if (di) out += '*'; else if (db) out += '**';
  if (spanChanges && (b.link !== null || spanClasses(b))) out += '[';
  return out;
}

// MARK: rebuilding inline markup

/** `MarkdownSpans.Piece`: text the reader sees in a style, a line break, or block syntax that's never styled. */
export type Piece = { kind: 'text'; text: string; style: InlineState } | { kind: 'newline'; text: string } | { kind: 'syntax'; text: string };

export interface LineSyntax { from: number; to: number; prefix: number; markers: [number, number][]; hardBreak: boolean }

/** The length of the list marker starting the line at `lineStart`, if the line is an item as `scan` reads it (in a
 *  block whose first line is one, not after a hard break). */
function listMarkerAt(t: string, lineStart: number): number | null {
  const lineEnd = lineEndOf(t, lineStart);
  if (!parseListMarker(t.slice(lineStart, Math.min(lineEnd, lineStart + 16)))) return null;
  let blockStart = lineStart;
  while (blockStart > 0) {
    const s = lineStartOf(t, blockStart - 1);
    if (trimmed(t.slice(s, blockStart - 1)).length === 0) break;
    blockStart = s;
  }
  const spans = scan(t.slice(blockStart, lineEnd));
  const m = spans.find((sp) => sp.kind === 'listMarker' && sp.from === lineStart - blockStart);
  return m ? m.to - m.from : null;
}

/** One line's inline syntax, as `scan` reads it (`MarkdownSpans.lineSyntax`). */
export function lineSyntax(t: string, location: number): LineSyntax {
  const loc = Math.min(Math.max(0, location), t.length);
  const from = lineStartOf(t, loc), to = lineEndOf(t, loc);
  const line = t.slice(from, to);
  const tr = trimmed(line);
  if (tr.length === 0 || isSceneBreak(tr) || fenceOf(tr) || tr === ':::') return { from, to, prefix: to - from, markers: [], hardBreak: false };
  let prefix = 0;
  if (line.startsWith('>')) prefix = line.startsWith('> ') ? 2 : 1;
  else if (line.startsWith('[^') && line.indexOf(']:') >= 0) prefix = line.indexOf(']:') + 2;
  else { const m = listMarkerAt(t, from); if (m !== null) prefix = m; }
  const spans: Span[] = [];
  inlineSpans(t, from + prefix, to, 0, spans);
  const markers: [number, number][] = [];
  for (const s of spans) if (s.kind === 'inlineMarker') markers.push([s.from, s.to]);
  // A trailing backslash that isn't itself escaped (the scanner leaves it: it escapes nothing on this line).
  const hardBreak = to - (from + prefix) > 0 && t.charCodeAt(to - 1) === BACKSLASH
    && !markers.some(([a]) => a === to - 2 && t.charCodeAt(to - 2) === BACKSLASH);
  return { from, to, prefix, markers, hardBreak };
}

/** Whether the character at `i` is hidden inline syntax (an emphasis asterisk, an escape, a span's brackets). */
export function isHiddenAt(t: string, i: number): boolean {
  if (i < 0 || i >= t.length || t.charCodeAt(i) === NL) return false;
  return lineSyntax(t, i).markers.some(([a, b]) => a <= i && i < b);
}

/** A hidden-syntax test over `t`, remembering each line's markers (the line scan is the costly part). Characters
 *  outside [lo, hi) are never hidden. */
export function hiddenTester(t: string, lo = 0, hi = t.length): (i: number) => boolean {
  const lines = new Map<number, LineSyntax>();
  return (i: number) => {
    if (i < lo || i >= hi || i >= t.length || t.charCodeAt(i) === NL) return false;
    const start = lineStartOf(t, i);
    let l = lines.get(start);
    if (!l) { l = lineSyntax(t, i); lines.set(start, l); }
    for (const [a, b] of l.markers) { if (a > i) break; if (i < b) return true; }
    return false;
  };
}

/** `[from, to)` grown to take in the emphasis and span markers touching its edges, so markers rebuilt for it can't
 *  merge with a run beside it. An escape's backslash is never taken in: it stays with what it escapes. */
export function expandedOverMarkers(t: string, from: number, to: number): [number, number] {
  let start = from, end = to;
  const first = lineSyntax(t, start);
  for (;;) {
    let m: [number, number] | undefined;
    for (let k = first.markers.length - 1; k >= 0; k--) { const x = first.markers[k]; if (x[0] < start && x[1] >= start) { m = x; break; } }
    if (!m || t.charCodeAt(m[0]) === BACKSLASH) break;
    start = m[0];
  }
  const last = end === start ? first : lineSyntax(t, end);
  for (;;) {
    const m = last.markers.find(([a, b]) => a <= end && b > end);
    if (!m || t.charCodeAt(m[0]) === BACKSLASH) break;
    end = m[1];
  }
  return [start, end];
}

/** The pieces of `t[from, to)` as the scanner reads them starting in `state` (`MarkdownSpans.pieces`). Emphasis and
 *  span markers are left out: they're what gets rebuilt. */
export function pieces(t: string, from: number, to: number, state: InlineState): Piece[] {
  const out: Piece[] = [];
  let st = { ...state };
  let location = from;
  const add = (s: string, style: InlineState) => {
    if (!s) return;
    const last = out[out.length - 1];
    if (last && last.kind === 'text' && stateEqual(last.style, style)) last.text += s;
    else out.push({ kind: 'text', text: s, style: { ...style } });
  };
  while (location <= to) {
    const line = lineSyntax(t, location);
    const lineEnd = line.to;
    const contentStart = line.from + line.prefix;
    // A hard break's backslash goes with its newline, so no marker can come between them.
    const breakAt = line.hardBreak ? lineEnd - 1 : lineEnd;
    if (location < contentStart) {
      const s = t.slice(location, Math.min(contentStart, to));
      if (s) out.push({ kind: 'syntax', text: s });
      location = Math.min(contentStart, to);
    }
    const stop = Math.max(location, Math.min(to, breakAt));
    let i = location, runStart = i;
    for (const [ma, mb] of line.markers) {
      if (!(mb > location && ma < stop)) continue;
      if (t.charCodeAt(ma) === BACKSLASH) continue;
      if (ma > runStart) add(t.slice(runStart, ma), st);
      if (ma >= i) {
        const c = t.charCodeAt(ma);
        if (c === STAR) { const n = mb - ma; if (n % 2 === 1) st.italic = !st.italic; if (n >= 2) st.bold = !st.bold; }
        else if (c === OPEN) {
          const close = bracketClose(t, ma + 1, lineEnd);
          Object.assign(st, close?.style ?? noSpan());
          st.link = close?.address ?? null;
        } else { Object.assign(st, noSpan()); st.link = null; }
      }
      i = Math.min(mb, stop);
      runStart = i;
    }
    if (stop > runStart) add(t.slice(runStart, stop), st);
    if (!(lineEnd < to)) {
      if (to > breakAt && location <= breakAt && breakAt < lineEnd) out.push({ kind: 'syntax', text: '\\' });
      break;
    }
    out.push({ kind: 'newline', text: line.hardBreak && location <= breakAt ? '\\\n' : '\n' });
    location = lineEnd + 1;
    st = plain();
  }
  return out;
}

/** Markdown for `ps` from style `before` to `after`, and where each piece lands (`MarkdownSpans.markdown`): each change
 *  of style is a single run of markers, and emphasis is closed before every line break. */
export function markdownOf(ps: Piece[], before: InlineState, after: InlineState): { text: string; spans: [number, number][] } {
  let out = '';
  let current = before;
  const spans: [number, number][] = [];
  for (const p of ps) {
    if (p.kind === 'text') {
      if (!stateEqual(p.style, current)) { out += transition(current, p.style); current = p.style; }
      const s = out.length;
      out += p.text;
      spans.push([s, out.length]);
    } else {
      if (!isPlain(current)) { out += transition(current, plain()); current = plain(); }
      const s = out.length;
      out += p.text;
      spans.push([s, out.length]);
    }
  }
  out += transition(current, after);
  return { text: out, spans };
}

/** Markdown for `[from, to)` that stands on its own (`MarkdownSpans.selfContained`): markers opened for the style it
 *  starts in and closed at its end, so pasting it anywhere styles only its own text. */
export function selfContained(t: string, from: number, to: number): string {
  const [a, b] = expandedOverMarkers(t, from, to);
  return markdownOf(pieces(t, a, b, stateAt(t, a)), plain(), plain()).text;
}

/** The text with its inline markers removed: what the reader sees (`MarkdownSpans.strippingInlineMarkers`). */
export function strippingInlineMarkers(markdown: string): string {
  const markers = scan(markdown).filter((s) => s.kind === 'inlineMarker').sort((x, y) => x.from - y.from);
  if (markers.length === 0) return markdown;
  let out = '';
  let i = 0;
  for (const m of markers) {
    if (m.from < i) continue;
    out += markdown.slice(i, m.from);
    i = m.to;
  }
  return out + markdown.slice(i);
}

/** The link at `location` (in its words, or at their end): the words' range and the address
 *  (`ScriveningsSession.link(at:)`). Null when there's none. */
export function linkAt(t: string, location: number): { from: number; to: number; address: string } | null {
  if (location < 0 || location > t.length) return null;
  const start = lineStartOf(t, location), end = lineEndOf(t, location);
  const spans: Span[] = [];
  inlineSpans(t, start, end, 0, spans);
  for (const s of spans) {
    if (s.kind === 'link' && s.from <= location && location <= s.to) return { from: s.from, to: s.to, address: s.address! };
  }
  return null;
}
